import React, { useState, useCallback } from 'react';
import { invoiceAPI } from '../../../services/api';
import { PageHeader } from '../../../components/layout/PageHeader';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const fmt = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
const METHODS = ['CASH','UPI','BANK_TRANSFER','CHEQUE','CARD','WALLET'];
const METHOD_COLORS = {
  CASH:'bg-green-100 text-green-700', UPI:'bg-blue-100 text-blue-700',
  BANK_TRANSFER:'bg-indigo-100 text-indigo-700', CHEQUE:'bg-yellow-100 text-yellow-700',
  CARD:'bg-purple-100 text-purple-700', WALLET:'bg-pink-100 text-pink-700',
};

export function CollectionReport() {
  const now = new Date();
  const [from,    setFrom]    = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
  const [to,      setTo]      = useState(now.toISOString().split('T')[0]);
  const [method,  setMethod]  = useState('');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoiceAPI.reportCollection({ from, to, paymentMethod: method || undefined });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [from, to, method]);

  const inp = 'px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const lbl = 'block text-xs font-semibold text-gray-500 uppercase mb-1.5';

  return (
    <div className="space-y-5">
      <PageHeader title="Collection Report" subtitle="Payments received — by method, date and invoice"/>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex gap-4 flex-wrap items-end">
          <div><label className={lbl}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} className={inp}/></div>
          <div><label className={lbl}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} className={inp}/></div>
          <div>
            <label className={lbl}>Payment Method</label>
            <select value={method} onChange={e=>setMethod(e.target.value)} className={inp}>
              <option value="">All Methods</option>
              {METHODS.map(m=><option key={m} value={m}>{m.replace('_',' ')}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            {[
              { label:'This Month', f:new Date(now.getFullYear(),now.getMonth(),1), t:now },
              { label:'Last Month', f:new Date(now.getFullYear(),now.getMonth()-1,1), t:new Date(now.getFullYear(),now.getMonth(),0) },
            ].map(r=>(
              <button key={r.label} onClick={()=>{setFrom(r.f.toISOString().split('T')[0]);setTo(r.t.toISOString().split('T')[0]);}}
                className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:bg-indigo-50 text-gray-600">{r.label}</button>
            ))}
          </div>
          <button onClick={load} disabled={loading}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {loading ? 'Loading...' : 'Generate'}
          </button>
        </div>
      </div>

      {!data && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <DocumentChartBarIcon className="w-12 h-12 mx-auto text-gray-200 mb-3"/>
          <p className="text-gray-400 text-sm">Select date range and click Generate</p>
        </div>
      )}

      {loading && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"/></div>}

      {data && !loading && <>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex justify-between items-center">
          <div>
            <p className="text-sm font-semibold text-green-800">Total Collected</p>
            <p className="text-xs text-green-600 mt-0.5">{data.rows?.length||0} payment transactions</p>
          </div>
          <p className="text-3xl font-bold text-green-700">{fmt(data.totalCollected)}</p>
        </div>

        {Object.keys(data.byMethod||{}).length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(data.byMethod).map(([m, amount])=>(
              <div key={m} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                <span className={`px-3 py-1.5 rounded-xl text-xs font-bold ${METHOD_COLORS[m]||'bg-gray-100 text-gray-600'}`}>
                  {m.replace('_',' ')}
                </span>
                <div>
                  <p className="font-bold text-gray-900">{fmt(amount)}</p>
                  <p className="text-xs text-gray-400">{data.rows?.filter(r=>r.paymentMethod===m).length} payments</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">Payment Transactions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['Date','Invoice No.','Customer','Method','Reference','Bank','Amount'].map(h=>(
                  <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left last:text-right">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.rows||[]).length===0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No payments found</td></tr>}
                {(data.rows||[]).map((row,i)=>(
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap">
                      {row.paymentDate?new Date(row.paymentDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—'}
                    </td>
                    <td className="px-4 py-2.5 font-mono font-semibold text-indigo-600 text-xs">{row.invoiceNumber}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{row.customerName||'—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-1 rounded-lg font-medium ${METHOD_COLORS[row.paymentMethod]||'bg-gray-100 text-gray-600'}`}>
                        {row.paymentMethod?.replace('_',' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{row.reference||'—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{row.bank||'—'}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-green-600">{fmt(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
              {(data.rows||[]).length > 0 && (
                <tfoot className="bg-gray-50 font-bold border-t-2 border-gray-200">
                  <tr>
                    <td className="px-4 py-2.5 text-gray-800" colSpan={6}>TOTAL ({data.rows.length} transactions)</td>
                    <td className="px-4 py-2.5 text-right text-green-700 text-base">{fmt(data.totalCollected)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </>}
    </div>
  );
}
